import React from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Icon, OptionalSection, ToastContext, Table, Message, TooltipHelp } from '@weezevent/nacre';
import { fetcher } from '@weezevent/weezjs-core';
import { PollingImportRequest } from './utils';
import LoadingCard from '../../../../components/loadingCard';

import { useTranslation } from 'react-i18next';
import css from './imports-steps.module.css';
import useAppContext from '../../../../context/useAppContext';

export const AnalysisStep = ({ organization, event, form, onCardDone, onDone, openModal }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [importReady, setImportReady] = React.useState(true);
    const [displayErrorsReport, setDisplayErrorsReport] = React.useState(false);
    const [context, setContext] = useAppContext();

    const reportValues = React.useMemo(() => {
        const { created, invalid, error } = form.requestDatas._summary;
        const summary = { created, invalid, error, total: created + invalid + error };
        return { ...summary, updated: form.requestDatas._data.attendee_update?.count || 0 };
    }, [form]);

    const _onDone = React.useCallback(
        requestDatas => {
            onDone(requestDatas, onCardDone);
        },
        [onCardDone, onDone]
    );
    const handlePatchImportRequest = React.useCallback(() => {
        fetcher
            .patch(form.importRequest.url, { data: { step_name: 'import', status: 'ready' } })
            .then(() => {
                const contextSyncApi = { ...context, apiSync: true };
                toast.success(t('ticket.attendees.imports.steps.analysis.import_in_progress'));
                const onSuccessFileAnalysis = () => {
                    const requestDatas = { ...form.requestDatas };
                    _onDone(requestDatas);
                };
                const onSuccess = resp => {
                    const { summary } = resp;
                    const { created, error, invalid } = summary;
                    onSuccessFileAnalysis();
                    setImportReady(true);
                    setContext(contextSyncApi);
                    toast.success(t('ticket.attendees.imports.steps.analysis.import_success'), {
                        text: (
                            <ul>
                                <li>{t('ticket.attendees.imports.steps.analysis.import_success_created', { count_created: created })}</li>
                                <li>{t('ticket.attendees.imports.steps.analysis.import_success_error', { count_error: error })}</li>
                                <li>{t('ticket.attendees.imports.steps.analysis.import_success_invalid', { count_invalid: invalid })}</li>
                            </ul>
                        )
                    });
                };
                const onError = () => {
                    setContext(contextSyncApi);
                    toast.error(t('ticket.common.toast_error'));
                };
                setImportReady(false);
                PollingImportRequest(fetcher, form, onSuccess, onError, true);
                history.push(`/ticket/O${organization.id}/E${event.id}/attendees/imports`);
            })
            .catch(e => {
                toast.error(t('ticket.common.toast_error'));
            });
    }, [organization, event, form, onCardDone, PollingImportRequest]);
    return (
        <div className={css['step-body']}>
            <LoadingCard loading={!importReady}>
                <ImportAnalysis datas={reportValues} />
                {reportValues.invalid >= 1 && (
                    <>
                        <Message title={t('ticket.attendees.imports.steps.analysis.message.title')} type="default">
                            {t('ticket.attendees.imports.steps.analysis.message.description')}
                        </Message>
                        <OptionalSection
                            className={css['analysis_report_section']}
                            label={t('ticket.attendees.imports.steps.analysis.report.display')}
                            labelOpen={t('ticket.attendees.imports.steps.analysis.report.hide')}
                            onToggle={() => setDisplayErrorsReport(!displayErrorsReport)}
                            open={displayErrorsReport}
                        >
                            <ErrorReportDetails errors={form.requestDatas._logs} />
                        </OptionalSection>
                    </>
                )}
                <div className={css['step-footer']}>
                    <Button
                        className={css['step-cta']}
                        primary
                        label={t(`ticket.attendees.imports.steps.analysis.cta.import_${reportValues.error > 0 ? 'errors' : 'valid'}`)}
                        onClick={handlePatchImportRequest}
                    />
                    <Button label={t('ticket.attendees.imports.steps.data.cta')} onClick={openModal} />
                </div>
            </LoadingCard>
        </div>
    );
};

const ImportAnalysis = ({ datas }) => {
    const { t } = useTranslation();

    const report = React.useMemo(() => {
        const checks = [
            { icon: 'chart', name: 'total', color: 'info', value: datas.total || 0 },
            { icon: 'check-circle', name: 'valid', color: 'valid', value: datas.created || 0 },
            { icon: 'info-circle', name: 'error', color: 'error', value: datas.invalid || 0 }
        ];
        const updated = { icon: 'refresh', name: 'updated', color: 'updated', value: datas.updated || 0 };

        if (datas.updated > 0) {
            checks.splice(2, 0, updated);
        }
        return checks;
    }, [datas]);

    return (
        <div className={css['analysis_infos']}>
            {report.map((ln, i) => (
                <p key={i}>
                    <Icon className={css[`analysis_icon_${ln.color}`]} name={ln.icon} />
                    <span className={css['analysis_message']}>{t(`ticket.attendees.imports.steps.analysis.report.${ln.name}`, { count: ln.value })}</span>
                    {ln.name === 'updated' && <TooltipHelp title={t('ticket.attendees.imports.steps.analysis.report.tooltip')} />}
                </p>
            ))}
        </div>
    );
};

const ErrorReportDetails = ({ errors }) => {
    const { t } = useTranslation();

    const filteredErrors = React.useMemo(() => {
        const report = errors.filter(it => it.type === 'invalid');

        return report.reduce((acc, item) => {
            const message = item.error[0].message.split(' ').join('_');
            const existingGroup = acc.find(group => group.value === message);
            if (existingGroup) {
                existingGroup.lines.push(item.line);
            } else {
                acc.push({
                    value: message,
                    error: t(`ticket.attendees.imports.steps.errors.keys.${message.toLowerCase()}`),
                    description: t(`ticket.attendees.imports.steps.errors.keys.${message.toLowerCase()}_desc`),
                    lines: [item.line]
                });
            }
            return acc;
        }, []);
    }, [errors]);

    return (
        <Table useNew>
            <Table.Header>
                <Table.Tr>
                    <th className={`${css['analysis_error_table_small']}`}>{t('ticket.attendees.imports.steps.errors.headers.lines')}</th>
                    <th className={`${css['analysis_error_table_medium']}`}>{t('ticket.attendees.imports.steps.errors.headers.error')}</th>
                    <th className={`${css['analysis_error_table_medium']}`}>{t('ticket.attendees.imports.steps.errors.headers.description')}</th>
                </Table.Tr>
            </Table.Header>
            <Table.TBody>
                {filteredErrors.map(err => (
                    <Table.Tr key={err.id} className={css['analysis_error_report']}>
                        <Table.Td>{err.lines.join(', ')}</Table.Td>
                        <Table.Td>{err.error}</Table.Td>
                        <Table.Td>{err.description}</Table.Td>
                    </Table.Tr>
                ))}
            </Table.TBody>
        </Table>
    );
};
